import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  SearchIcon,
  ChartLineIcon,
  VideoIcon,
  MicIcon,
  BriefcaseBusinessIcon,
} from '../icons';
import './action-search-bar.css';

/* ── Types ────────────────────────────────────────────────── */

export interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  description?: string;
  shortcut?: string;
  tag?: string;
}

export interface ActionSearchDropdownProps {
  query: string;
  isFocused: boolean;
  actions?: QuickAction[];
  onActionSelect?: (action: QuickAction) => void;
}

/* ── Default Actions ──────────────────────────────────────── */

const defaultActions: QuickAction[] = [
  {
    id: 'search-mentors',
    label: 'Search Mentors',
    icon: <SearchIcon size={14} />,
    description: 'Find professionals',
    shortcut: '⌘K',
    tag: 'Search',
  },
  {
    id: 'book-session',
    label: 'Book a Session',
    icon: <VideoIcon size={14} />,
    description: 'Referral or mock interview',
    tag: 'Action',
  },
  {
    id: 'resume-review',
    label: 'Resume Review',
    icon: <ChartLineIcon size={14} />,
    description: 'Get expert feedback',
    tag: 'Service',
  },
  {
    id: 'explore-companies',
    label: 'Explore Companies',
    icon: <BriefcaseBusinessIcon size={14} />,
    description: 'Browse by company',
    tag: 'Browse',
  },
  {
    id: 'voice-search',
    label: 'Voice Search',
    icon: <MicIcon size={14} />,
    description: 'Speak to search',
    tag: 'Input',
  },
];

/* ── Component ────────────────────────────────────────────── */

export function ActionSearchDropdown({
  query,
  isFocused,
  actions = defaultActions,
  onActionSelect,
}: ActionSearchDropdownProps) {
  const [filteredActions, setFilteredActions] = useState<QuickAction[]>(actions);

  useEffect(() => {
    if (!query.trim()) {
      setFilteredActions(actions);
      return;
    }
    const normalizedQuery = query.toLowerCase().trim();
    const filtered = actions.filter(
      (action) =>
        action.label.toLowerCase().includes(normalizedQuery) ||
        action.description?.toLowerCase().includes(normalizedQuery)
    );
    setFilteredActions(filtered);
  }, [query, actions]);

  const showDropdown = isFocused && filteredActions.length > 0;

  return (
    <AnimatePresence>
      {showDropdown && (
        <motion.div
          className="action-search-dropdown"
          initial={{ opacity: 0, y: -8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.96 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        >
          <ul className="action-search-dropdown__list">
            {filteredActions.map((action, index) => (
              <motion.li
                key={action.id}
                className="action-search-dropdown__item"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.04 }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onActionSelect?.(action);
                }}
              >
                <div className="action-search-dropdown__item-left">
                  <span className="action-search-dropdown__item-icon">
                    {action.icon}
                  </span>
                  <span className="action-search-dropdown__item-label">
                    {action.label}
                  </span>
                  {action.description && (
                    <span className="action-search-dropdown__item-desc">
                      {action.description}
                    </span>
                  )}
                </div>
                <div className="action-search-dropdown__item-right">
                  {action.shortcut && (
                    <span className="action-search-dropdown__item-shortcut">
                      {action.shortcut}
                    </span>
                  )}
                  {action.tag && (
                    <span className="action-search-dropdown__item-tag">
                      {action.tag}
                    </span>
                  )}
                </div>
              </motion.li>
            ))}
          </ul>
          <div className="action-search-dropdown__footer">
            <span>⌘K to open commands</span>
            <span>ESC to close</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ActionSearchDropdown;
